/**
 * The two moving parts of the blog landing page (`sections/ora-blog.liquid`).
 *
 * `ora-blog-more` is the Load more button. It fetches the next page of this
 * section alone (`?section_id=`), copies its cards onto the end of the grid,
 * and takes the next page's own button as the new one -- so the count, the
 * href and whether there is a further page all come from the server. Without
 * script the button is a plain link to `?page=2`, which is also what a crawler
 * follows.
 *
 * The topic tabs, and each topic card's "See all", swap the section in place
 * from the tagged URL rather than reloading the page, and push that URL so
 * the address bar, back and forward all agree with what is shown. Each tagged
 * page is a real server-rendered page of its own, which is what makes a topic
 * indexable; the swap only saves the reload.
 */

/**
 * @param {string} url
 * @param {string} sectionId
 * @returns {Promise<Element | null>} The section's root from the fetched copy
 */
async function fetchSection(url, sectionId) {
  const target = new URL(url, window.location.origin);
  target.searchParams.set('section_id', sectionId);
  const response = await fetch(target.toString(), { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  return doc.querySelector('[data-ora-blog]');
}

class OraBlogMore extends HTMLElement {
  connectedCallback() {
    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  /** @param {MouseEvent} event */
  async onClick(event) {
    const link = /** @type {HTMLElement} */ (event.target).closest?.('a[href]');
    const root = this.closest('[data-ora-blog]');
    const grid = root?.querySelector('[data-ora-blog-grid]');
    if (!(link instanceof HTMLAnchorElement) || !root || !grid) return;
    if (this.getAttribute('aria-busy') === 'true') {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.setAttribute('aria-busy', 'true');
    try {
      const next = await fetchSection(link.href, root.getAttribute('data-section-id') || '');
      const items = next ? Array.from(next.querySelectorAll('[data-ora-blog-grid] [data-ora-blog-item]')) : [];
      const nextMore = next?.querySelector('ora-blog-more');
      if (!items.length || !nextMore) throw new Error('nothing to append');

      const first = items[0];
      grid.append(...items.map((item) => document.importNode(item, true)));

      // The next page counts its own cards from its offset, so the total it
      // prints is already right for everything now on screen.
      const shown = this.querySelector('[data-ora-blog-shown]');
      const nextShown = nextMore.querySelector('[data-ora-blog-shown]');
      if (shown && nextShown) shown.textContent = nextShown.textContent;
      const button = this.querySelector('a[href]');
      const nextButton = nextMore.querySelector('a[href]');
      if (nextButton && button) {
        button.setAttribute('href', nextButton.getAttribute('href') || '');
      } else {
        button?.remove();
      }

      // Move focus to the first new card, so a keyboard reader carries on
      // from where the list grew rather than staying on a button.
      const firstLink = grid.querySelector(
        `[data-ora-blog-item]:nth-child(${grid.children.length - items.length + 1}) a`
      );
      if (first && firstLink instanceof HTMLElement) firstLink.focus({ preventScroll: true });
    } catch {
      // Fall back to the page the link names rather than a silent dead end.
      window.location.href = link.href;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('ora-blog-more')) customElements.define('ora-blog-more', OraBlogMore);

/**
 * @param {string} url
 * @param {boolean} push
 */
async function swapTo(url, push) {
  const root = document.querySelector('[data-ora-blog]');
  if (!root) return;
  const sectionId = root.getAttribute('data-section-id') || '';
  root.setAttribute('aria-busy', 'true');
  try {
    const fresh = await fetchSection(url, sectionId);
    if (!fresh) throw new Error('no section');
    root.replaceWith(document.importNode(fresh, true));
    if (push) history.pushState({ oraBlog: true }, '', url);
    const tabs = document.querySelector('[data-ora-blog] .ora-blog__tabs');
    const current = tabs?.querySelector('[aria-current="page"]');
    if (current instanceof HTMLElement) {
      current.focus({ preventScroll: true });
      current.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  } catch {
    window.location.href = url;
  }
}

document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = /** @type {HTMLElement} */ (event.target).closest?.('a[data-ora-blog-tab]');
  if (!(link instanceof HTMLAnchorElement)) return;
  if (link.origin !== window.location.origin) return;
  event.preventDefault();
  if (link.getAttribute('aria-current') === 'page') return;
  swapTo(link.href, true);
});

window.addEventListener('popstate', () => {
  if (document.querySelector('[data-ora-blog]')) swapTo(window.location.href, false);
});
